import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Loader2 } from 'lucide-react'
import api from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { formatDate, formatDateTime } from '../../utils/helpers'

/**
 * A company's front page — the first thing their people see after signing in at their own address.
 * Their own numbers (routers/approvals.py /front): what waits for them, what was sent back to them, what
 * waits for the team to agree, which dossiers are ready to submit; the documents filed week by week; and
 * the approvals waiting, a click from acting on them.
 */

function Tile({ value, label, to, tone = 'text-gray-900' }) {
  const body = (
    <div className="bg-white border border-gray-200 rounded-xl p-4 h-full hover:border-blue-300">
      <p className={`text-3xl font-bold ${tone}`}>{value}</p>
      <p className="text-sm text-gray-600 mt-1">{label}</p>
    </div>
  )
  return to ? <Link to={to} className="block">{body}</Link> : body
}

// One series, so one colour and no legend: the title names it. Bars are thin, the axis recessive, and only
// the first and last weeks are labelled; every week's number is on hover.
function FiledPerWeek({ weeks }) {
  const [hover, setHover] = useState(null)
  const max = Math.max(1, ...weeks.map(w => w.filed))
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <h2 className="text-sm font-semibold text-gray-800">Documents filed per week</h2>
      <div className="relative mt-4 h-32 flex items-end gap-2 border-b border-gray-200" role="img"
           aria-label={weeks.map(w => `week of ${formatDate(w.week)}: ${w.filed}`).join('; ')}>
        {weeks.map((w, i) => (
          <div key={w.week} className="flex-1 h-full flex items-end justify-center cursor-default"
               onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <div className={`w-full max-w-[28px] rounded-t-sm ${hover === i ? 'bg-blue-700' : 'bg-blue-500'}`}
                 style={{ height: `${Math.max(w.filed ? 4 : 1, (w.filed / max) * 100)}%` }} />
          </div>
        ))}
        {hover !== null && (
          <div className="absolute -top-2 px-2 py-1 rounded-md bg-gray-900 text-white text-xs whitespace-nowrap pointer-events-none"
               style={{ left: `${((hover + 0.5) / weeks.length) * 100}%`, transform: 'translate(-50%, -100%)' }}>
            Week of {formatDate(weeks[hover].week)}: <b>{weeks[hover].filed}</b> filed
          </div>
        )}
      </div>
      <div className="flex justify-between text-[11px] text-gray-500 mt-1">
        <span>{weeks.length} weeks ago</span><span>This week</span>
      </div>
    </div>
  )
}

export default function CompanyHome() {
  const { user } = useAuth()
  const [f, setF] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    api.get('/approvals/front').then(r => setF(r.data)).catch(() => setError('Could not load your front page.'))
  }, [])

  if (error) return <p className="p-6 text-sm text-red-600">{error}</p>
  if (!f) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{user?.organisation?.name || 'Front page'}</h1>
        <p className="text-sm text-gray-500 mt-1">Hello {user?.full_name}. Here is what is waiting today.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Tile value={f.waiting} label="Waiting for your approval" to="/approvals" tone={f.waiting ? 'text-blue-700' : 'text-gray-900'} />
        <Tile value={f.sent_back} label="Sent back, waiting for a new version" to="/approvals" tone={f.sent_back ? 'text-red-700' : 'text-gray-900'} />
        <Tile value={f.agree || f.proposed} label={f.agree ? 'Waiting for you to agree a move' : 'Waiting for the team to agree a move'} to="/approvals" />
        <Tile value={`${f.ready} of ${f.open}`} label="Open dossiers ready to submit" to="/teams" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <FiledPerWeek weeks={f.weeks} />
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-800">Approvals: waiting for you</h2>
            <Link to="/approvals" className="text-xs text-blue-600 hover:underline flex items-center gap-1">All <ArrowRight className="w-3 h-3" /></Link>
          </div>
          {f.approve.length === 0
            ? <p className="px-4 py-4 text-sm text-gray-400">Nothing waits for your approval.</p>
            : <ul className="divide-y divide-gray-100">
                {f.approve.map(r => (
                  <li key={r.item_id} className="px-4 py-2.5">
                    <Link to={r.link} className="text-sm text-gray-900 hover:text-blue-700">{r.title}</Link>
                    <p className="text-xs text-gray-500">
                      {r.dossier_title} · {r.folder === 'submission' ? 'Submission folder, final' : 'Working folder'}
                      {r.version > 1 ? ` · version ${r.version}` : ''}{r.since ? ` · ${formatDateTime(r.since)}` : ''}
                    </p>
                  </li>
                ))}
              </ul>}
        </div>
      </div>

      {f.dossiers.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100"><h2 className="text-sm font-semibold text-gray-800">Open dossiers</h2></div>
          <ul className="divide-y divide-gray-100">
            {f.dossiers.map(d => (
              <li key={d.id} className="px-4 py-2.5 flex items-center gap-3">
                <Link to={`/teams/${d.team_id}?dossier=${d.id}`} className="mr-auto text-sm text-gray-900 hover:text-blue-700">
                  {d.title} <span className="text-xs font-mono text-gray-400">{d.dossier_number}</span>
                </Link>
                <span className="text-xs text-gray-500">{d.counts.in_submission ?? 0} in the Submission folder</span>
                {d.ready && <span className="text-[11px] px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200">Ready to submit</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
